import { API_URL } from '../../config';
import React, { useState } from 'react';

import { formatPortalDate } from '../../utils/dateUtils';

const getApiBase = () =>
  API_URL;

// ── Types ─────────────────────────────────────────────────────────────────────
interface TrackedRequest {
  referenceNumber: string;
  status: 'pending' | 'supervisor_approved' | 'fleet_approved' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  purpose: string;
  destination: string;
  startDateTime: string;
  endDateTime: string;
  requesterName: string;
  department?: string;
  priority: string;
  passengerCount?: number;
  assignedVehicle?: {
    plateNumber: string;
    make: string;
    model: string;
    color?: string;
    category?: string;
  };
  assignedDriver?: {
    name: string;
    phone?: string | null;
  };
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  companyDomain: string;
  onBack: () => void;
}

// ── Timeline config ───────────────────────────────────────────────────────────
const TIMELINE_STEPS = [
  {
    key: 'submitted',
    label: 'Request Submitted',
    desc: 'Your request has been received',
    activeStatuses: ['pending'],
    doneStatuses:   ['supervisor_approved', 'fleet_approved', 'in_progress', 'completed'],
  },
  {
    key: 'review',
    label: 'Under Review',
    desc: 'Fleet manager is reviewing your request',
    activeStatuses: ['supervisor_approved'],
    doneStatuses:   ['fleet_approved', 'in_progress', 'completed'],
  },
  {
    key: 'approved',
    label: 'Approved & Assigned',
    desc: 'Vehicle and driver have been assigned',
    activeStatuses: ['fleet_approved'],
    doneStatuses:   ['in_progress', 'completed'],
  },
  {
    key: 'trip',
    label: 'Trip Underway',
    desc: 'Vehicle is on the road',
    activeStatuses: ['in_progress'],
    doneStatuses:   ['completed'],
  },
  {
    key: 'completed',
    label: 'Trip Completed',
    desc: 'Trip has been completed successfully',
    activeStatuses: [],
    doneStatuses:   ['completed'],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getStepState = (step: typeof TIMELINE_STEPS[0], status: string): 'done' | 'active' | 'pending' => {
  if (step.doneStatuses.includes(status))   return 'done';
  if (step.activeStatuses.includes(status)) return 'active';
  return 'pending';
};

const fmtShort = (dt: string) => formatPortalDate(dt);
  
const getStatusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    pending:             { cls: 'pending',   label: 'Pending Review' },
    supervisor_approved: { cls: 'review',    label: 'Under Review' },
    fleet_approved:      { cls: 'approved',  label: 'Approved' },
    in_progress:         { cls: 'active',    label: 'In Progress' },
    completed:           { cls: 'completed', label: 'Completed' },
    rejected:            { cls: 'rejected',  label: 'Rejected' },
    cancelled:           { cls: 'cancelled', label: 'Cancelled' },
  };
  return map[status] ?? { cls: 'pending', label: status };
};

const isTerminal = (s: string) => ['rejected', 'cancelled'].includes(s);
const isCancellable = (s: string) => ['pending', 'supervisor_approved'].includes(s);


// ── Main component ────────────────────────────────────────────────────────────
const RequestTracker: React.FC<Props> = ({ companyDomain, onBack }) => {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [email, setEmail]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');
  const [trackedRequest, setTrackedRequest]   = useState<TrackedRequest | null>(null);

  // Cancel state
  const [cancelConfirm, setCancelConfirm]     = useState(false);
  const [cancelling, setCancelling]           = useState(false);
  const [cancelError, setCancelError]         = useState('');

  const handleTrack = async () => {
    const ref = referenceNumber.trim().toUpperCase();
    const em  = email.trim();

    if (!ref) { setError('Enter your reference number (e.g. REQ-2026-00001)'); return; }
    if (!em)  { setError('Enter the email address you used when submitting'); return; }

    setLoading(true);
    setError('');
    setTrackedRequest(null);

    try {
      const res  = await fetch(`${getApiBase()}/public/${companyDomain}/track/${encodeURIComponent(ref)}?email=${encodeURIComponent(em)}`);
      const data = await res.json();

      if (data.success) {
        setTrackedRequest(data.data);
      } else {
        setError(data.message || 'Request not found. Please check your reference number and email address.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!trackedRequest) return;
    setCancelling(true);
    setCancelError('');

    try {
      const res  = await fetch(`${getApiBase()}/public/${companyDomain}/cancel/${encodeURIComponent(trackedRequest.referenceNumber)}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (data.success) {
        setTrackedRequest({ ...trackedRequest, status: 'cancelled' });
        setCancelConfirm(false);
      } else {
        setCancelError(data.message || 'Failed to cancel request.');
      }
    } catch {
      setCancelError('Network error. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReset = () => {
    setTrackedRequest(null);
    setError('');
    setCancelConfirm(false);
    setCancelError('');
  };

  return (
    <div className="tracker-view">
      {/* Back button */}
      <button className="back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Back
      </button>

      {/* Search form */}
      <div className="tracker-form-card">
        <h2 className="tracker-title">Track Request</h2>
        <p className="tracker-subtitle">
          Enter your reference number and the email you used when submitting.
        </p>

        <div className="tracker-form-fields">
          <div className="field-group">
            <label className="field-label">Reference Number</label>
            <input
              className="field-input"
              type="text"
              placeholder="e.g. REQ-2026-00001"
              value={referenceNumber}
              onChange={e => setReferenceNumber(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTrack()}
              style={{ fontFamily: "'SF Mono','Fira Code',monospace", fontWeight: 700, letterSpacing: '0.04em' }}
            />
          </div>

          <div className="field-group">
            <label className="field-label">Email Address</label>
            <input
              className="field-input"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTrack()}
            />
          </div>
        </div>

        {error && <div className="tracker-error">{error}</div>}

        <button className="btn btn--primary btn--full" onClick={handleTrack} disabled={loading}>
          {loading ? <><div className="spinner-sm" />Searching...</> : <>Find Request <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></>}
        </button>
      </div>

      {/* Result */}
      {trackedRequest && (
        <div className="request-card">
          {/* Header */}
          <div className="request-card__header">
            <span className="request-ref">{trackedRequest.referenceNumber}</span>
            <StatusBadge status={trackedRequest.status} />
          </div>

          <div className="request-card__body">
            {/* Meta */}
            <div className="request-meta">
              <div className="meta-row">
                <span className="meta-label">Purpose</span>
                <span className="meta-value">{trackedRequest.purpose}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Destination</span>
                <span className="meta-value">{trackedRequest.destination}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Trip Start</span>
                <span className="meta-value">{fmtShort(trackedRequest.startDateTime)}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Trip End</span>
                <span className="meta-value">{fmtShort(trackedRequest.endDateTime)}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Submitted</span>
                <span className="meta-value">{fmtShort(trackedRequest.createdAt)}</span>
              </div>
            </div>

            {/* Terminal states */}
            {isTerminal(trackedRequest.status) && (
              <TerminalState
                status={trackedRequest.status as 'rejected' | 'cancelled'}
                reason={trackedRequest.rejectionReason}
              />
            )}

            {/* Normal timeline */}
            {!isTerminal(trackedRequest.status) && (
              <div className="timeline">
                <p className="timeline-title">Status</p>
                <div className="timeline-steps">
                  {TIMELINE_STEPS.map(step => {
                    const state = getStepState(step, trackedRequest.status);
                    return (
                      <div key={step.key} className={`timeline-step timeline-step--${state}`}>
                        <div className="timeline-icon">
                          {state === 'done' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M5 12l5 5 9-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            </svg>
                          ) : state === 'active' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="4" fill="currentColor" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          )}
                        </div>
                        <div className="timeline-content">
                          <p className="timeline-label">{step.label}</p>
                          <p className="timeline-desc">{step.desc}</p>
                          {step.key === 'approved' && state !== 'pending' && trackedRequest.assignedVehicle && (
                            <AssignmentCard request={trackedRequest} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Cancel section ── */}
            {isCancellable(trackedRequest.status) && !cancelConfirm && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-2)' }}>
                <button
                  className="btn btn--ghost btn--full"
                  onClick={() => setCancelConfirm(true)}
                  style={{ color: 'var(--danger)', borderColor: 'var(--danger-bd)' }}
                >
                  Cancel this Request
                </button>
              </div>
            )}

            {/* Cancel confirmation */}
            {cancelConfirm && (
              <div style={{
                marginTop: 16, padding: '16px',
                background: 'var(--danger-bg)', border: '1px solid var(--danger-bd)',
                borderRadius: 'var(--r-sm)',
              }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
                  Cancel this request?
                </p>
                <p style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 14, lineHeight: 1.5 }}>
                  This will cancel <strong>{trackedRequest.referenceNumber}</strong>.
                  This action cannot be undone.
                </p>

                {cancelError && (
                  <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 10, fontWeight: 600 }}>
                    {cancelError}
                  </p>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn btn--ghost"
                    style={{ flex: 1 }}
                    onClick={() => { setCancelConfirm(false); setCancelError(''); }}
                    disabled={cancelling}
                  >
                    Keep Request
                  </button>
                  <button
                    className="btn btn--primary"
                    style={{ flex: 1, background: 'var(--danger)', boxShadow: 'none' }}
                    onClick={handleCancel}
                    disabled={cancelling}
                  >
                    {cancelling ? <><div className="spinner-sm" />Cancelling...</> : 'Yes, Cancel'}
                  </button>
                </div>
              </div>
            )}

            {/* Track another */}
            <div className="tracker-new-request" style={{ marginTop: 16 }}>
              <button className="btn btn--ghost btn--full" onClick={handleReset}>
                Track a different request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { cls, label } = getStatusBadge(status);
  return (
    <span className={`status-badge status-badge--${cls}`}>
      <span className="status-dot" />
      {label}
    </span>
  );
};

const AssignmentCard: React.FC<{ request: TrackedRequest }> = ({ request }) => (
  <div className="assignment-card">
    <p className="assignment-card__title">Your Assignment</p>

    {request.assignedVehicle && (
      <>
        <div className="assignment-row">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M5 17h14M7 17l1-5h8l1 5M9 12l1-4h4l1 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="8" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="16" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <span>Plate</span>
          <span style={{ fontFamily: "'SF Mono',monospace", letterSpacing: '0.04em' }}>
            {request.assignedVehicle.plateNumber}
          </span>
        </div>
        <div className="assignment-row">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          <span>Vehicle</span>
          <span>{request.assignedVehicle.make} {request.assignedVehicle.model}</span>
        </div>
        {request.assignedVehicle.color && (
          <div className="assignment-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
            </svg>
            <span>Color</span>
            <span>{request.assignedVehicle.color}</span>
          </div>
        )}
      </>
    )}

    {request.assignedDriver?.name && request.assignedDriver.name !== 'TBD' && (
      <>
        <div className="assignment-row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(13,148,136,0.2)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
            <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span>Driver</span>
          <span>{request.assignedDriver.name}</span>
        </div>
        {request.assignedDriver.phone && (
          <div className="assignment-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M6.6 10.8a15.2 15.2 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10 21 3 14 3 5c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span>Phone</span>
            <a href={`tel:${request.assignedDriver.phone}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: 700 }}>
              {request.assignedDriver.phone}
            </a>
          </div>
        )}
      </>
    )}
  </div>
);

const TerminalState: React.FC<{ status: 'rejected' | 'cancelled'; reason?: string }> = ({ status, reason }) => {
  const isRejected = status === 'rejected';
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="timeline">
        <p className="timeline-title">Status</p>
        <div className="timeline-steps">
          <div className="timeline-step timeline-step--done">
            <div className="timeline-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5 9-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="timeline-content">
              <p className="timeline-label">Request Submitted</p>
              <p className="timeline-desc">Your request was received</p>
            </div>
          </div>
          <div className="timeline-step timeline-step--error">
            <div className="timeline-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="timeline-content">
              <p className="timeline-label" style={{ color: 'var(--danger)' }}>
                Request {isRejected ? 'Rejected' : 'Cancelled'}
              </p>
              <p className="timeline-desc">
                {isRejected ? 'Your request was not approved' : 'This request was cancelled'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {reason && (
        <div className="rejection-card">
          <p className="rejection-title">{isRejected ? 'Reason for Rejection' : 'Cancellation Note'}</p>
          <p className="rejection-reason">{reason}</p>
        </div>
      )}

      <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--info-bg)', border: '1px solid var(--info-bd)', borderRadius: 'var(--r-sm)', fontSize: 13, color: 'var(--info-text)', fontWeight: 500, lineHeight: 1.5 }}>
        You can submit a new vehicle request from the home screen.
      </div>
    </div>
  );
};

export default RequestTracker;